/**
 * Before checkout, sync line item delivery properties with current delivery-inline rules
 * (metafield custom.pdp_delivery_time, stock, market overrides), then proceed to checkout.
 */
(function () {
  'use strict';

  let skipNextCheck = false;

  function findSyncDataScript(btn) {
    const drawer = btn.closest('cart-drawer');
    if (drawer) return drawer.querySelector('script[data-cart-delivery-sync]');
    const mainCart = btn.closest('main-cart');
    if (mainCart) return mainCart.querySelector('script[data-cart-delivery-sync]');
    return document.querySelector('script[data-cart-delivery-sync]');
  }

  function parseConfig(btn) {
    const el = findSyncDataScript(btn);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch {
      return null;
    }
  }

  function isCartCheckoutButton(btn) {
    if (!btn || btn.getAttribute('name') !== 'checkout') return false;
    if (btn.closest('cart-drawer')) return true;
    if (btn.getAttribute('form') === 'cart') return true;
    return false;
  }

  function normalizeProperties(raw) {
    const out = {};
    if (!raw) return out;
    if (Array.isArray(raw)) {
      raw.forEach(function (p) {
        if (p && p.name != null) out[String(p.name)] = p.value != null ? String(p.value) : '';
      });
      return out;
    }
    Object.keys(raw).forEach(function (k) {
      const v = raw[k];
      if (v != null && v !== '') out[k] = String(v);
    });
    return out;
  }

  function deliveryMatches(props, expected, keyNormal, keyDelayed) {
    const otherKey = expected.key === keyNormal ? keyDelayed : keyNormal;
    const targetVal = String(expected.value).trim();
    const curTarget = String(props[expected.key] ?? '').trim();
    const curOther = String(props[otherKey] ?? '').trim();
    return curTarget === targetVal && curOther === '';
  }

  function buildMergedProperties(cartItem, expected, keyNormal, keyDelayed) {
    const next = normalizeProperties(cartItem.properties);
    delete next[keyNormal];
    delete next[keyDelayed];
    next[expected.key] = expected.value;
    return next;
  }

  function getStorefrontRoot() {
    if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    if (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.root_url) {
      return FoxTheme.routes.root_url;
    }
    return '/';
  }

  function getMarketQueryString() {
    const params = new URLSearchParams(window.location.search);
    const preserved = new URLSearchParams();
    ['oc', 'country', 'currency'].forEach(function (key) {
      const val = params.get(key);
      if (val) preserved.set(key, val);
    });
    const qs = preserved.toString();
    return qs ? '?' + qs : '';
  }

  function appendMarketParams(url) {
    const qs = getMarketQueryString();
    if (!qs) return url;
    return url + (url.indexOf('?') === -1 ? qs : '&' + qs.slice(1));
  }

  function getCartJsUrl() {
    return appendMarketParams(getStorefrontRoot() + 'cart.js');
  }

  function getCartChangeUrl() {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.cart_change_url) {
      return appendMarketParams(FoxTheme.routes.cart_change_url);
    }
    return appendMarketParams(getStorefrontRoot() + 'cart/change');
  }

  function getAjaxFetchConfig() {
    const base =
      typeof FoxTheme !== 'undefined' && FoxTheme.utils && FoxTheme.utils.fetchConfig
        ? FoxTheme.utils.fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return {
      ...base,
      credentials: 'same-origin',
      headers: {
        ...base.headers,
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  async function fetchCartJson() {
    const url = getCartJsUrl();
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status + ' ' + url);
    return res.json();
  }

  async function changeLine(line, quantity, properties) {
    const changeUrl = getCartChangeUrl();
    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      properties: properties,
    });
    const res = await fetch(changeUrl, {
      ...getAjaxFetchConfig(),
      body,
    });
    const data = await res.json().catch(function () {
      return {};
    });
    if (!res.ok || (data.status != null && Number(data.status) >= 400)) {
      throw new Error(data.description || data.message || 'change');
    }
    return data;
  }

  function buildDeliveryUpdates(cart, cfg) {
    const lines = Array.isArray(cfg.lines) ? cfg.lines : [];
    const keyNormal = cfg.keyNormal || 'Lieferzeit';
    const keyDelayed = cfg.keyDelayed || 'Verlängerte Lieferzeit';
    const updates = [];

    if (!cart.items || cart.items.length === 0) return updates;

    for (let i = 0; i < lines.length; i++) {
      const spec = lines[i];
      const idx = spec.line - 1;
      if (idx < 0 || idx >= cart.items.length) continue;
      const item = cart.items[idx];
      const props = normalizeProperties(item.properties);
      if (deliveryMatches(props, spec, keyNormal, keyDelayed)) continue;
      updates.push({
        line: spec.line,
        quantity: item.quantity,
        properties: buildMergedProperties(item, spec, keyNormal, keyDelayed),
      });
    }
    return updates;
  }

  async function applyDeliveryUpdates(updates) {
    for (let u = 0; u < updates.length; u++) {
      await changeLine(updates[u].line, updates[u].quantity, updates[u].properties);
    }
  }

  /**
   * Programmatic button.click() often fails to submit (non-trusted event, or button associated
   * via form="…" outside the form). requestSubmit runs the normal submit path without re-entering
   * our document click listener.
   */
  function getCheckoutForm(btn) {
    const formId = btn.getAttribute('form');
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }
    if (btn.form) return btn.form;
    return btn.closest('form');
  }

  const SPINNER_HTML =
    '<div class="loading__spinner hidden">' +
    '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>' +
    '</svg>' +
    '</div>';

  function ensureSpinner(btn) {
    if (!btn || btn.querySelector('.loading__spinner')) return;
    btn.insertAdjacentHTML('beforeend', SPINNER_HTML);
  }

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    ensureSpinner(btn);
    const spinner = btn.querySelector('.loading__spinner');
    if (loading) {
      btn.classList.add('btn--loading');
      btn.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.classList.remove('btn--loading');
      btn.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  function proceedToCheckout(btn) {
    const form = getCheckoutForm(btn);
    if (form && typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(btn);
        return;
      } catch (err) {
        console.warn('[cart-delivery-sync] requestSubmit failed, using synthetic click', err);
      }
    }
    skipNextCheck = true;
    btn.click();
  }

  document.addEventListener(
    'click',
    async function (e) {
      const t = e.target;
      if (!t || !t.closest) return;
      const btn = t.closest('button[name="checkout"]');
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      if (!isCartCheckoutButton(btn)) return;

      if (skipNextCheck) {
        skipNextCheck = false;
        return;
      }

      const cfg = parseConfig(btn);
      if (!cfg || cfg.enabled === false) return;

      const specLines = Array.isArray(cfg.lines) ? cfg.lines : [];
      if (specLines.length === 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      setButtonLoading(btn, true);

      try {
        const cart = await fetchCartJson();
        const updates = buildDeliveryUpdates(cart, cfg);

        if (updates.length > 0) {
          await applyDeliveryUpdates(updates);
        }

        proceedToCheckout(btn);
      } catch (err) {
        console.warn('[cart-delivery-sync] sync failed, proceeding to checkout', err);
        setButtonLoading(btn, false);
        proceedToCheckout(btn);
      }
    },
    true
  );
})();
